import { z } from 'zod';
import { EnvironmentSchema } from '../domain/application.js';
import { EvidenceSchema, FindingStatusSchema, SeveritySchema } from '../domain/finding.js';
import { ApprovalDecisionSchema, ApprovalGateSchema } from './approval.js';
import { IdSchema, IsoDateTimeSchema, OrgIdSchema, UserIdSchema } from './common.js';
import { TriageSummarySchema } from './finding.js';
import { ScanKindSchema, ScanStatusSchema } from './scan.js';
import { ScopeAuthorisationSchema, ScopeLimitsSchema, ScopePermitsSchema, ScopeTargetsSchema, TestWindowSchema, } from './scope.js';
/**
 * A report: a fixed snapshot of an engagement's evidence, for a technical or audit reader.
 *
 * Built by code from stored records — never written by the language model — so every sentence
 * in it can be traced to a row, and the same evidence always produces the same report. It is
 * hashed when generated, stored under that hash, and approved by that hash at the Report gate:
 * what was published is exactly what was approved, and that can be checked afterwards.
 *
 * Every string from a target (titles, URLs, evidence excerpts) is data. Renderers must escape
 * it; nothing in a report is markup.
 */
export const REPORT_FORMAT = 'guardian-ai/report@1';
export const ReportAudienceSchema = z.enum(['TECHNICAL']);
const ReportFindingSchema = z.object({
    id: IdSchema,
    fingerprint: z.string(),
    title: z.string(),
    severity: SeveritySchema,
    status: FindingStatusSchema,
    ruleId: z.string().optional(),
    description: z.string().optional(),
    remediation: z.string().optional(),
    evidence: EvidenceSchema.optional(),
    firstSeenAt: IsoDateTimeSchema,
    lastSeenAt: IsoDateTimeSchema,
    triage: TriageSummarySchema.optional(),
});
export const ReportSchema = z.object({
    format: z.literal(REPORT_FORMAT),
    audience: ReportAudienceSchema,
    version: z.number().int().positive(),
    generatedAt: IsoDateTimeSchema,
    generatedByUserId: UserIdSchema,
    generatedByEmail: z.string().optional(),
    engagement: z.object({
        id: IdSchema,
        orgId: OrgIdSchema,
        name: z.string(),
        environment: EnvironmentSchema,
        applicationId: IdSchema,
        applicationName: z.string().optional(),
    }),
    /** The approved scope the testing was authorised by, with the hash its approval recorded. */
    scope: z.object({
        version: z.number().int().positive(),
        digest: z.string().length(64),
        targets: ScopeTargetsSchema,
        limits: ScopeLimitsSchema,
        windows: z.array(TestWindowSchema),
        permits: ScopePermitsSchema,
        authorisation: ScopeAuthorisationSchema,
    }),
    approvals: z.array(z.object({
        gate: ApprovalGateSchema,
        decision: ApprovalDecisionSchema,
        scopeVersion: z.number().int().positive(),
        scopeDigest: z.string().optional(),
        reportVersion: z.number().int().positive().optional(),
        approverUserId: UserIdSchema,
        approverEmail: z.string().optional(),
        at: IsoDateTimeSchema,
        note: z.string().optional(),
    })),
    scans: z.array(z.object({
        id: IdSchema,
        kind: ScanKindSchema,
        status: ScanStatusSchema,
        scopeVersion: z.number().int().positive(),
        requestedAt: IsoDateTimeSchema,
        startedAt: IsoDateTimeSchema.optional(),
        finishedAt: IsoDateTimeSchema.optional(),
        findingCount: z.number().int().nonnegative().optional(),
    })),
    findings: z.array(ReportFindingSchema),
    /** Every triage decision, oldest first: how each finding came to its final status. */
    triage: z.array(TriageSummarySchema.extend({ findingId: IdSchema, fingerprint: z.string() })),
    summary: z.object({
        total: z.number().int().nonnegative(),
        bySeverity: z.record(SeveritySchema, z.number().int().nonnegative()),
        byStatus: z.record(FindingStatusSchema, z.number().int().nonnegative()),
    }),
});
/** What is stored in the table about a report; the report itself is in the evidence bucket. */
export const ReportMetaSchema = z.object({
    engagementId: IdSchema,
    orgId: OrgIdSchema,
    version: z.number().int().positive(),
    /** SHA-256 of the report's canonical JSON. Also part of its storage key. */
    digest: z.string().length(64),
    s3Key: z.string(),
    audience: ReportAudienceSchema,
    generatedAt: IsoDateTimeSchema,
    generatedByUserId: UserIdSchema,
    findingCount: z.number().int().nonnegative(),
});
export const ListReportsResponseSchema = z.object({ items: z.array(ReportMetaSchema) });
/**
 * GET /v1/engagements/{id}/reports/{version}
 *
 * `integrity` is worked out by the server on every read, not stored. `intact` means the stored
 * bytes still hash to the recorded digest; `approvedDigestMatches` says whether that digest is
 * the one an approver recorded, and is absent while no decision exists.
 */
export const GetReportResponseSchema = z.object({
    meta: ReportMetaSchema,
    report: ReportSchema,
    integrity: z.object({
        intact: z.boolean(),
        approvedDigestMatches: z.boolean().optional(),
    }),
});
export const ReportVersionPathSchema = z.strictObject({
    id: IdSchema,
    version: z.coerce.number().int().positive(),
});
//# sourceMappingURL=report.js.map